// OV2-14 — THE PHOTO A PHONE ACTUALLY TAKES.
//
// Lives in its own module (like src/askLalaHandoff.js) so the fast gate can assert the limits without
// importing TSX. One definition of "how big may an uploaded photo be", used by the form and checked by
// the gate, so what we send and what the server accepts cannot drift apart.
//
// WHY THIS EXISTS, measured. Analyze Your Reading sent the camera's original file, base64'd, in one JSON
// body. A 4032x3024 iPhone shot is ~9.6MB, which is a 12.86MB request — over the server's 11.2MB cap. The
// upload was refused, and (before the server fix) the refusal could not even be delivered, so every real
// phone photo died as "your reading could not be prepared right now". Desktop looked fine only because
// desktop test images are small. Three phones, three accounts, every time.
//
// So the browser resizes BEFORE the upload. This is not a workaround for the cap; it is the correct thing
// to send. The vision model behind the reading (claude-sonnet-4-6) takes images at a maximum of 1568px on
// the long edge and downsamples anything bigger itself — so every pixel past that was paid for in upload
// time on a phone's cellular connection and then thrown away. A spread photo at 1568px is ~300-600KB.

// The vendor's own long-edge limit. Sending more buys nothing; it is discarded server-side.
export const MAX_PHOTO_EDGE = 1568;
// JPEG quality for the re-encode. High enough that card faces and pip counts stay legible to the model.
export const PHOTO_JPEG_QUALITY = 0.85;
// R15: `MAX_PHOTO_BYTES = 8 * 1024 * 1024` used to sit here — a second copy of the server's
// MAX_ATTACHMENT_BYTES, carrying a comment that said "the gate holds these two numbers together."
// It did not. Nothing in the repo read this constant at all; what the gate really holds is
// MAX_PHOTO_EDGE against the server's cap (smoke.mjs: the worst-case re-encode lands an order of
// magnitude under it), and that check is untouched. A number nobody reads, wearing a claim that it
// is watched, is worse than no number — so it is gone rather than left standing beside the law.

// How many pixels to draw, preserving aspect ratio. Never enlarges a small photo — upscaling adds bytes
// and no detail, and a genuinely tiny image should reach the server so the pre-gate can ask for a better
// one in Lala's own words rather than being silently padded into looking acceptable.
export function fitWithin(width, height, maxEdge = MAX_PHOTO_EDGE) {
  const w = Number(width) || 0;
  const h = Number(height) || 0;
  if (w <= 0 || h <= 0) return { width: 0, height: 0, resized: false };
  const longest = Math.max(w, h);
  if (longest <= maxEdge) return { width: w, height: h, resized: false };
  const scale = maxEdge / longest;
  return { width: Math.round(w * scale), height: Math.round(h * scale), resized: true };
}

// Decode with the EXIF orientation applied, so a photo taken holding the phone sideways is not read
// upside down. createImageBitmap does this properly where it exists; the <img> fallback gets it from the
// browser's own default image-orientation handling.
async function decode(file) {
  if (typeof createImageBitmap === "function") {
    try { return await createImageBitmap(file, { imageOrientation: "from-image" }); }
    catch { /* fall through — some browsers reject the options bag, some cannot decode the format */ }
    try { return await createImageBitmap(file); } catch { /* fall through */ }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("decode failed"));
      img.src = url;
    });
  } finally { URL.revokeObjectURL(url); }
}

function toDataUrl(canvas, quality) {
  return canvas.toDataURL("image/jpeg", quality);
}

// THE ONE ENTRY POINT. Returns { dataUrl, base64, filename, bytes, width, height, resized, fellBack }.
//
// FAIL SOFT, ALWAYS. If the browser cannot decode the file (an HEIC on a browser without HEIC support is
// the real case), we send the ORIGINAL bytes rather than nothing: the server sniffs the type, converts
// HEIC itself, and answers honestly if it truly cannot read it. A resize failure must never be the reason
// a member cannot get her spread read — that is the bug this whole item was about.
export async function preparePhotoForUpload(file, { maxEdge = MAX_PHOTO_EDGE, quality = PHOTO_JPEG_QUALITY } = {}) {
  const filename = (file && file.name) || "spread.jpg";
  const original = await readAsDataUrl(file);
  const fallback = {
    dataUrl: original,
    base64: original.split(",")[1] || "",
    filename, bytes: file ? file.size : 0,
    width: 0, height: 0, resized: false, fellBack: true,
  };

  try {
    const bitmap = await decode(file);
    const sw = bitmap.width || bitmap.naturalWidth || 0;
    const sh = bitmap.height || bitmap.naturalHeight || 0;
    const box = fitWithin(sw, sh, maxEdge);
    if (!box.width || !box.height) return fallback;

    const canvas = document.createElement("canvas");
    canvas.width = box.width;
    canvas.height = box.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return fallback;
    // White underneath: a transparent PNG flattened onto black would bury the cards.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, box.width, box.height);
    ctx.drawImage(bitmap, 0, 0, box.width, box.height);
    if (typeof bitmap.close === "function") bitmap.close();

    const dataUrl = toDataUrl(canvas, quality);
    const base64 = dataUrl.split(",")[1] || "";
    if (!base64) return fallback;
    return {
      dataUrl, base64, filename,
      bytes: Math.round(base64.length * 0.75),
      width: box.width, height: box.height,
      resized: box.resized, fellBack: false,
    };
  } catch {
    return fallback;
  }
}

// ---------------------------------------------------------------------------
// ITEM 4(c) — CROP TO SQUARE, AND THE ARITHMETIC IS ITS OWN FUNCTION.
//
// It lives here beside `fitWithin` rather than inside the component that shows the picker, for the
// reason every law in this repo has been pulled out of a TSX file: a crop written inside a component
// can only be checked by rendering the component, and this one has an off-by-one in it that nothing
// would ever see — a centre crop that is one pixel out looks exactly right on every photograph.
//
// CENTRE CROP, NEVER A STRETCH. Squashing a portrait into a square is the one outcome a member
// notices instantly and cannot fix, because it is her own face.
// ---------------------------------------------------------------------------
export function squareCropBox(width, height) {
  const w = Number(width) || 0;
  const h = Number(height) || 0;
  if (w <= 0 || h <= 0) return { sx: 0, sy: 0, size: 0 };
  const size = Math.min(w, h);
  return { sx: Math.round((w - size) / 2), sy: Math.round((h - size) / 2), size };
}

// THE ONE ENTRY POINT FOR A PROFILE PHOTO. Same fail-soft contract as `preparePhotoForUpload` with
// one deliberate difference: **an undecodable file is REFUSED here rather than sent whole.** The
// spread photo falls back to the original bytes because a reading she paid for must not die on a
// resize; an avatar has no such stake, and sending the raw file would put an uncropped, possibly
// enormous, possibly HEIC image into a 40px circle on every post she has ever made.
export async function prepareAvatarForUpload(file, { edge = 512, quality = PHOTO_JPEG_QUALITY } = {}) {
  try {
    const bitmap = await decode(file);
    const sw = bitmap.width || bitmap.naturalWidth || 0;
    const sh = bitmap.height || bitmap.naturalHeight || 0;
    const box = squareCropBox(sw, sh);
    if (!box.size) return null;
    // Never enlarge: a 90px avatar drawn at 512 is a blurred 90px avatar in more bytes.
    const out = Math.min(edge, box.size);

    const canvas = document.createElement("canvas");
    canvas.width = out;
    canvas.height = out;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, out, out);
    ctx.drawImage(bitmap, box.sx, box.sy, box.size, box.size, 0, 0, out, out);
    if (typeof bitmap.close === "function") bitmap.close();

    const dataUrl = toDataUrl(canvas, quality);
    const base64 = dataUrl.split(",")[1] || "";
    if (!base64) return null;
    return {
      dataUrl, base64,
      contentType: "image/jpeg",
      bytes: Math.round(base64.length * 0.75),
      width: out, height: out,
    };
  } catch {
    return null;
  }
}

function readAsDataUrl(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => resolve("");
    reader.readAsDataURL(file);
  });
}

// LR16 — **THE TWO FILE HELPERS EVERY MEDIA COMPOSER IN THIS HOUSE NEEDS, DECLARED ONCE.** Both were
// private to `src/components/LivingRoom.tsx`, so the inbox composer would have had to re-type them —
// and a second `fileToBase64` is a second answer key for the one thing every upload on this site
// does (R28/R29, and R47's count-the-copies, which is what found `saveToRoom` typed twenty-four
// times). They live here because this module is already "prepare a file for upload"; they are the
// half of it that is not a picture.

// The base64 PAYLOAD alone — never the `data:` prefix, because the server decodes with
// `Buffer.from(str, "base64")` and a prefix left on is bytes that are not the file.
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || "").split(",")[1] || "");
    reader.onerror = () => reject(new Error("read failed"));
    reader.readAsDataURL(file);
  });
}

// **A DURATION THAT COULD NOT BE READ IS 0 AND NEVER A THROW**, deliberately: the cap above this is
// what refuses a long clip, and a browser that cannot read metadata off a container must not cost
// her the send. The object URL is revoked, because a composer that opens twenty clips otherwise
// leaks twenty of them (R56's ruling one media over).
export function videoDuration(file) {
  return new Promise((resolve) => {
    const el = document.createElement("video");
    el.preload = "metadata";
    el.onloadedmetadata = () => { URL.revokeObjectURL(el.src); resolve(el.duration || 0); };
    el.onerror = () => { URL.revokeObjectURL(el.src); resolve(0); };
    el.src = URL.createObjectURL(file);
  });
}
